import {
  isCurrentDateInRange,
  parseDate,
  toISODateOnly,
} from '../../../../utils/dateUtils';
import {
  AbsenceApprovalStatus,
  useTimeTrackingSettingsAllDaysQuery,
  WorkloadTrackingType,
} from '../../__generated__/graphql';
import { getWorkloadForDate } from '../../utils/timesheetHelper';
import {
  TimeTrackingSettings,
  mapTimeTrackingSettings,
} from './useTimeTrackingSettings';
import { useNamespacedTranslation } from '../../../../hooks/useNamespacedTranslation';
import { useTimeTrackingContext } from './TimeTrackingContext';

type TimeTrackingType = 'timeTracking' | 'projectTracking';

export const useTimeTrackingSettingsAllDays = () => {
  const { employeeId } = useTimeTrackingContext();

  const { data, loading } = useTimeTrackingSettingsAllDaysQuery({
    variables: {
      employeeId,
    },
  });

  const t = useNamespacedTranslation('timeTracking.timeTrackingRestriction');

  const getTimeTrackingSettingForDay = (
    day: Date,
  ): TimeTrackingSettings | undefined => {
    if (!data) {
      return undefined;
    }
    const workloads = data.workloads;
    const workloadForDate = getWorkloadForDate(workloads, day);
    return mapTimeTrackingSettings(workloadForDate);
  };

  const hasOngoingRestrictedAbsenceForDay = (
    day: string,
    now?: Date,
  ): boolean => {
    if (!data) {
      return false;
    }
    if (!now) {
      now = new Date();
    }
    const dateToCheck = parseDate(day);
    // if date is today then checking also time otherwise check only by day start
    const isCheckingForToday = toISODateOnly(now) === day;
    if (isCheckingForToday) {
      dateToCheck.setHours(now.getHours());
      dateToCheck.setMinutes(now.getMinutes());
      dateToCheck.setSeconds(now.getSeconds());
      return data.employeeAbsences.some(
        (item) =>
          item.flair__Approval_Status__c !== AbsenceApprovalStatus.Rejected &&
          item.flair__Category_Time_Tracking_Restricted__c &&
          isCurrentDateInRange(
            dateToCheck,
            item.flair__Start_Date__c,
            item.flair__End_Date__c,
            item.flair__Start_Time__c,
            item.flair__End_Time__c,
          ),
      );
    } else {
      return data.employeeAbsences.some(
        (item) =>
          item.flair__Approval_Status__c !== AbsenceApprovalStatus.Rejected &&
          item.flair__Category_Time_Tracking_Restricted__c &&
          isCurrentDateInRange(
            dateToCheck,
            item.flair__Start_Date__c,
            item.flair__End_Date__c,
            '00:00:00.000',
            '23:59:59.000',
          ),
      );
    }
  };

  const getTrackingRestrictedReason = (
    day: string,
    timeTrackingType: TimeTrackingType,
    now?: Date,
  ): string | null => {
    const dayTimeTrackingSettings = getTimeTrackingSettingForDay(
      parseDate(day),
    );

    if (!dayTimeTrackingSettings) {
      return t('noTimeTrackingSettings');
    }
    if (hasOngoingRestrictedAbsenceForDay(day, now)) {
      return t('absenceScheduled');
    }
    const workload = dayTimeTrackingSettings.workload;
    // todo: In next PR check active employments
    if (!workload) {
      return t('noWorkloadDefined');
    }
    const workloadTrackingType = workload.flair__Time_Tracking_Type__c;
    const validWorkloadTypes =
      timeTrackingType === 'timeTracking'
        ? [
            WorkloadTrackingType.TimeTracking,
            WorkloadTrackingType.TimeTrackingAndProjectTracking,
            WorkloadTrackingType.TimeTrackingWithProjects,
          ]
        : [
            WorkloadTrackingType.ProjectTracking,
            WorkloadTrackingType.TimeTrackingAndProjectTracking,
            WorkloadTrackingType.TimeTrackingWithProjects,
          ];
    const isTimeTrackingEnabled =
      validWorkloadTypes.includes(workloadTrackingType);
    if (!isTimeTrackingEnabled) {
      return t('timeTrackingNotEnabled');
    }

    if (day < workload.flair__Start_Date__c) {
      return t('noWorkloadDefined');
    }
    if (workload.flair__End_Date__c && day > workload.flair__End_Date__c) {
      return t('noWorkloadDefined');
    }
    return null;
  };

  return {
    loading,
    getAllTimeTrackingSettings: (): TimeTrackingSettings[] | undefined => {
      if (!data) {
        return undefined;
      }
      const workloads = data.workloads ?? [];
      return workloads.map(mapTimeTrackingSettings);
    },
    getTimeTrackingSettingForDay,
    getTimeTrackingRestrictedReason: (day: string, now?: Date): string | null =>
      getTrackingRestrictedReason(day, 'timeTracking', now),
    getProjectTrackingRestrictedReason: (
      day: string,
      now?: Date,
    ): string | null =>
      getTrackingRestrictedReason(day, 'projectTracking', now),
  };
};
